import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TABS = ["Store", "Pricing", "Automation", "Email", "Users"];

export default function Settings() {
  const [tab, setTab] = useState("Store");
  return (
    <div className="space-y-5 max-w-3xl">
      <div><h1 className="font-display text-2xl font-bold tracking-tight">Settings</h1></div>
      <div className="flex gap-1 border-b border-zinc-200">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === t ? "border-zinc-900" : "border-transparent text-zinc-500"}`} data-testid={`settings-tab-${t}`}>{t}</button>
        ))}
      </div>
      {tab === "Store" && <StoreSettings />}
      {tab === "Pricing" && <PricingSettings />}
      {tab === "Automation" && <AutomationSettings />}
      {tab === "Email" && <EmailSettings />}
      {tab === "Users" && <UserSettings />}
    </div>
  );
}

function StoreSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings/store").then((r) => setS(r.data)); }, []);
  const save = async () => { try { await api.put("/admin/settings/store", s); toast.success("Saved"); } catch (e) { toast.error(apiErr(e)); } };
  if (!s) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Store Name</Label><Input value={s.store_name || ""} onChange={(e) => setS({ ...s, store_name: e.target.value })} data-testid="store-name" /></div>
        <div><Label>Contact Email</Label><Input value={s.contact_email || ""} onChange={(e) => setS({ ...s, contact_email: e.target.value })} /></div>
        <div><Label>Contact Phone</Label><Input value={s.contact_phone || ""} onChange={(e) => setS({ ...s, contact_phone: e.target.value })} /></div>
        <div><Label>Primary Currency</Label>
          <Select value={s.primary_currency} onValueChange={(v) => setS({ ...s, primary_currency: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{["USD", "NGN", "EUR", "GBP"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Free Shipping Threshold (USD)</Label><Input type="number" value={s.free_shipping_threshold || 0} onChange={(e) => setS({ ...s, free_shipping_threshold: parseFloat(e.target.value) })} /></div>
      </div>
      <Button onClick={save} data-testid="save-store-settings">Save Store Settings</Button>
    </div>
  );
}

function PricingSettings() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/admin/pricing").then((r) => setP(r.data)); }, []);
  const save = async () => { try { await api.put("/admin/pricing", p); toast.success("Pricing rules saved"); } catch (e) { toast.error(apiErr(e)); } };
  if (!p) return null;
  const setRule = (i, k, v) => { const rules = [...p.rules]; rules[i] = { ...rules[i], [k]: k === "type" ? v : parseFloat(v) }; setP({ ...p, rules }); };
  const addRule = () => setP({ ...p, rules: [...p.rules, { min: 0, max: 1000, type: "multiplier", value: 2 }] });
  const delRule = (i) => setP({ ...p, rules: p.rules.filter((_, idx) => idx !== i) });

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-5">
      <div>
        <div className="flex justify-between items-center mb-3"><Label>Markup Rules by Supplier Price</Label><Button size="sm" variant="outline" onClick={addRule} data-testid="add-rule"><Plus size={13} className="mr-1" />Rule</Button></div>
        <div className="space-y-2">
          {p.rules.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-sm" data-testid={`rule-${i}`}>
              <span className="text-zinc-400 text-xs">$</span>
              <Input type="number" value={r.min} onChange={(e) => setRule(i, "min", e.target.value)} className="w-20 h-9" />
              <span className="text-zinc-400 text-xs">to $</span>
              <Input type="number" value={r.max} onChange={(e) => setRule(i, "max", e.target.value)} className="w-24 h-9" />
              <Select value={r.type} onValueChange={(v) => setRule(i, "type", v)}>
                <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="multiplier">× Multiplier</SelectItem><SelectItem value="percentage">% Markup</SelectItem><SelectItem value="fixed">$ Fixed add</SelectItem></SelectContent>
              </Select>
              <Input type="number" step="0.1" value={r.value} onChange={(e) => setRule(i, "value", e.target.value)} className="w-20 h-9" />
              <button onClick={() => delRule(i)} className="text-zinc-400 hover:text-red-600"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Minimum Profit per Order (USD)</Label><Input type="number" value={p.min_profit} onChange={(e) => setP({ ...p, min_profit: parseFloat(e.target.value) })} data-testid="min-profit" /></div>
        <div><Label>Minimum Profit %</Label><Input type="number" value={p.min_profit_pct} onChange={(e) => setP({ ...p, min_profit_pct: parseFloat(e.target.value) })} /></div>
        <div><Label>Max Supplier Price Increase %</Label><Input type="number" value={p.max_supplier_increase_pct} onChange={(e) => setP({ ...p, max_supplier_increase_pct: parseFloat(e.target.value) })} /></div>
      </div>
      <div className="flex gap-6">
        <div className="flex items-center gap-2"><Switch checked={p.shipping_included} onCheckedChange={(v) => setP({ ...p, shipping_included: v })} /><Label>Include shipping in price</Label></div>
        <div className="flex items-center gap-2"><Switch checked={p.psychological} onCheckedChange={(v) => setP({ ...p, psychological: v })} /><Label>Psychological pricing (.99)</Label></div>
      </div>
      <Button onClick={save} data-testid="save-pricing">Save Pricing Rules</Button>
    </div>
  );
}

function AutomationSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings/store").then((r) => setS(r.data)); }, []);
  const save = async (mode) => { const next = { ...s, fulfillment_mode: mode }; setS(next); try { await api.put("/admin/settings/store", next); toast.success(`Fulfillment: ${mode}`); } catch (e) { toast.error(apiErr(e)); } };
  if (!s) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 space-y-4">
      <Label>Order Fulfillment Mode</Label>
      {[["semi_automatic", "Semi-Automatic (Recommended)", "Paid orders wait for admin to review & approve before the supplier order is placed."],
        ["automatic", "Fully Automatic", "Verified paid orders are sent to the supplier automatically. Orders failing stock/profit checks are flagged for review."]].map(([mode, title, desc]) => (
        <button key={mode} onClick={() => save(mode)} className={`w-full text-left border rounded-xl p-4 transition-colors ${s.fulfillment_mode === mode ? "border-zinc-900 bg-zinc-50" : "border-zinc-200"}`} data-testid={`fulfillment-${mode}`}>
          <div className="flex items-center gap-2"><div className={`w-4 h-4 rounded-full border-2 ${s.fulfillment_mode === mode ? "border-zinc-900 bg-zinc-900" : "border-zinc-300"}`} /><span className="font-semibold text-sm">{title}</span></div>
          <p className="text-xs text-zinc-500 mt-1 ml-6">{desc}</p>
        </button>
      ))}
    </div>
  );
}

function EmailSettings() {
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/admin/email-templates").then((r) => setTemplates(r.data)); }, []);
  const save = async (t) => { try { await api.put(`/admin/email-templates/${t.key}`, { subject: t.subject, body: t.body }); toast.success("Template saved"); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500">Templates use variables like {"{{name}}"}, {"{{order_number}}"}, {"{{tracking_number}}"}. Email sending is prepared (templates stored); live delivery can be enabled later.</p>
      {templates.map((t, i) => (
        <div key={t.key} className="bg-white border border-zinc-200 rounded-xl p-4" data-testid={`email-template-${t.key}`}>
          <p className="text-sm font-semibold capitalize mb-2">{t.key.replace(/_/g, " ")}</p>
          <Input value={t.subject} onChange={(e) => { const n = [...templates]; n[i] = { ...t, subject: e.target.value }; setTemplates(n); }} className="mb-2" />
          <Textarea value={t.body} onChange={(e) => { const n = [...templates]; n[i] = { ...t, body: e.target.value }; setTemplates(n); }} className="mb-2 text-xs" />
          <Button size="sm" variant="outline" onClick={() => save(templates[i])}>Save</Button>
        </div>
      ))}
    </div>
  );
}

function UserSettings() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "PRODUCT_MANAGER" });
  const load = () => api.get("/admin/admin-users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/roles").then((r) => setRoles(r.data)); }, []);
  const create = async () => { try { await api.post("/admin/admin-users", form); toast.success("Admin user created"); setForm({ name: "", email: "", password: "", role: "PRODUCT_MANAGER" }); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="space-y-4">
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-zinc-500 bg-zinc-50 border-b"><th className="p-3 font-medium">Name</th><th className="p-3 font-medium">Email</th><th className="p-3 font-medium">Role</th></tr></thead>
          <tbody>{users.map((u) => <tr key={u.id} className="border-b border-zinc-50"><td className="p-3 font-medium">{u.name}</td><td className="p-3 text-zinc-600">{u.email}</td><td className="p-3 text-xs">{u.role?.replace(/_/g, " ")}</td></tr>)}</tbody>
        </table>
      </div>
      <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-3">
        <p className="font-semibold text-sm">Add Team Member</p>
        <div className="grid grid-cols-2 gap-3">
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" />
          <Input type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
            <SelectContent>{roles.map((r) => <SelectItem key={r.role} value={r.role}>{r.role.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={create} data-testid="create-user-btn">Create User</Button>
      </div>
    </div>
  );
}
